import { useMemo } from "react";
import { LivenessWebVerifyStatusEnum } from "../constants/enums";

export interface RegtankLiveness {
  clientSessionId: string;
  sessionId: string;
  expiredDate: string;
  livenessStatus: LivenessWebVerifyStatusEnum | null;
  noOfAttemptLiveness: number;
  reattemptApproval: string | null;
  regtankLivenessUrl: string;
}

export interface RegtankLivenessSession {
  sessionId: string;
  latestLiveness: RegtankLiveness;
  isExpired: boolean;
  regtankLivenessUrl: string;
}

/**
 * Custom hook to manage Regtank liveness session data
 * Extracts and processes liveness verification session information
 *
 * @param documentId - The document ID to match against clientSessionId
 * @param allLivenessData - Array of all liveness records for the user
 * @returns RegtankLivenessSession object or null if no matching session found
 */
export const useRegtankLivenessSession = (
  documentId: number,
  allLivenessData: RegtankLiveness[] | undefined
): RegtankLivenessSession | null => {
  return useMemo(() => {
    if (!documentId || !allLivenessData) return null;

    // Find all liveness records matching the document ID
    const matchedLiveness = allLivenessData.filter(
      (x) => x.clientSessionId.toString() === documentId.toString()
    );

    if (matchedLiveness.length === 0) return null;

    // Sort by expiredDate to get the most recent session
    const sortedLiveness =
      matchedLiveness.length > 1
        ? matchedLiveness.sort(
            (a, b) =>
              new Date(b.expiredDate).getTime() -
              new Date(a.expiredDate).getTime()
          )
        : matchedLiveness;

    const latestLiveness = sortedLiveness[0];

    // Check if session exists in sessionStorage, otherwise use latest from data
    const sessionStorageId = sessionStorage.getItem("regtankSessionId");
    const sessionId = sessionStorageId || latestLiveness.sessionId;

    // Check if the session is expired
    const isExpired = latestLiveness.expiredDate
      ? new Date(latestLiveness.expiredDate) < new Date()
      : false;

    return {
      sessionId,
      latestLiveness,
      isExpired,
      regtankLivenessUrl: latestLiveness.regtankLivenessUrl,
    };
  }, [documentId, allLivenessData]);
};
